/**
 * LÍMITES DE LA BIBLIOTECA DE MEDIOS — topes, umbrales, tipos, claves y textos, en un solo sitio
 * para la UI y el backend (C4, 28-oct-2026).
 *
 * ⚠️ COPIA BYTE-IDÉNTICA en `src/utils/limitesDeBiblioteca.js`. Cualquier cambio va en las dos, y
 * `functions/scripts/check-shared-module-parity.js` bloquea el commit si divergen. La copia de
 * `src/` es `.js` porque CRA no resuelve `.cjs`; el contenido es el mismo.
 *
 * POR QUÉ COMPARTIDO: la UI avisa ANTES de subir (un PDF de 120 MB no debería viajar para que el
 * servidor lo rechace al final) y el servidor decide DESPUÉS, sobre los bytes que quedaron en
 * Storage (`bot/media/bibliotecaDeMedios.js`). Si los números divergieran, la UI dejaría subir lo
 * que el servidor rechaza, o callaría un aviso que el servidor sí da.
 *
 * Los topes son los de Meta (documentación leída el 28-oct-2026): PDF 100 MB, imagen 5 MB, audio
 * 16 MB; la nota de voz sólo lleva botón de reproducir hasta 512 KB.
 *
 * PURO y sin dependencias: ni Buffer ni nada del navegador.
 */

const MB = 1024 * 1024;

const LIMITES = Object.freeze({ pdf: 100 * MB, imagen: 5 * MB, audio: 16 * MB });

// Aviso NO bloqueante desde aquí: tus clientes tardarán en descargarlo (decisión de Rodrigo, 28-oct).
const AVISO_PESADO_DESDE = 10 * MB;

// Por encima, la nota de voz llega con un icono de descarga en vez del botón de reproducir.
const VOZ_CON_BOTON_MAX = 512 * 1024;

// 10 (era 5; decisión de Rodrigo, 29-oct). Cabe en el plazo de los archivos (`envioDeArchivos.js`,
// 100 s desde el inicio del trigger): el peor caso —PDF de 14 MB + 10 fotos de 700 KB + audio— se
// estimó en ~45 s con los tiempos más lentos medidos contra Meta, y el turno más lento de 30 días llegó
// a `respondido` a los 35 s (test `envioDeArchivos`, «el peor caso con 10 fotos»). El límite que SÍ
// pesa es otro: Meta permite 1 mensaje cada 6 s al mismo usuario y una ráfaga se paga después a ese
// ritmo (error 131056); texto + PDF + 10 fotos + audio son 13 mensajes seguidos.
const MAX_FOTOS_POR_GRUPO = 10;

const TIPOS = Object.freeze(['pdf', 'imagen', 'audio']);

// El content-type que se FIRMA por tipo. Es el que Meta verá: si no coincide con los bytes, el
// envío falla después del 200 (131053).
const MIMES_POR_TIPO = Object.freeze({
  pdf: Object.freeze(['application/pdf']),
  imagen: Object.freeze(['image/jpeg', 'image/png', 'image/heic']),
  audio: Object.freeze(['audio/ogg', 'audio/mp4', 'audio/mpeg', 'audio/wav']),
});

// CONVERSIÓN AL SUBIR (decisión de Rodrigo, 3-nov-2026): un M4A, MP3 o WAV se convierte a nota de voz
// (OGG Opus, mono, 24 kbps, sin metadatos) y un HEIC a JPG, en el servidor, al finalizar la subida. Lo que
// se guarda y se manda es SIEMPRE el convertido; Meta no ve nunca el original. Estos mimes se firman tal
// cual para el PUT del original (`bot/media/convertirMedio.js` hace la conversión).
const CONVERTIBLES = Object.freeze({
  'audio/mp4': 'audio', 'audio/mpeg': 'audio', 'audio/wav': 'audio', 'image/heic': 'imagen',
});
// El tope del ORIGINAL que se convierte: un WAV pesa diez veces su nota de voz, y una foto HEIC grande se
// reduce al convertirla. El convertido tiene que caber igual en `LIMITES` (lo comprueba el servidor).
const LIMITES_DE_ENTRADA = Object.freeze({ audio: 50 * MB, imagen: 20 * MB });

/** El tope de lo que se SUBE: el de entrada si el mime se convierte, el de Meta si no. */
function limiteDeEntrada(tipo, mime) {
  return CONVERTIBLES[mime] === tipo ? LIMITES_DE_ENTRADA[tipo] : LIMITES[tipo];
}

// Minúsculas, dígitos y guion bajo, de 3 a 60, sin `__` ni guion bajo en los extremos: es lo que
// la tool recibe como `enum` y lo que compone el id del documento (Firestore reserva `__…__`).
const PATRON_CLAVE = /^[a-z0-9](?:[a-z0-9]|_(?!_)){1,58}[a-z0-9]$/;

function esClaveValida(clave) {
  return typeof clave === 'string' && PATRON_CLAVE.test(clave);
}

// Lo que ve quien sube el archivo. Dice qué hacer, no sólo qué falló.
const MENSAJES = Object.freeze({
  vacio: 'El archivo está vacío.',
  'formato-no-admitido': 'Formato no admitido. Sube un PDF, una foto JPG, PNG o HEIC, o un audio OGG (Opus), M4A, MP3 o WAV.',
  'png-sin-cabecera': 'El PNG está dañado: no se puede leer su cabecera.',
  'png-no-8-bits': 'El PNG no es de 8 bits por canal. Expórtalo de nuevo como PNG estándar.',
  'png-no-rgb': 'El PNG no está en color RGB (es en escala de grises o con paleta). Expórtalo de nuevo en color.',
  'jpeg-ilegible': 'El JPG está dañado: no se puede leer su cabecera.',
  'jpeg-no-8-bits': 'El JPG no es de 8 bits. Expórtalo de nuevo como JPG estándar.',
  'jpeg-cmyk': 'El JPG está en CMYK (formato de imprenta). Expórtalo de nuevo en RGB.',
  'jpeg-no-rgb': 'El JPG no está en color RGB. Expórtalo de nuevo en color.',
  'ogg-ilegible': 'El OGG está dañado: no se puede leer su cabecera.',
  'ogg-no-opus': 'El OGG no está en Opus. Las notas de voz tienen que ser OGG con códec Opus.',
  'ogg-no-mono': 'La nota de voz no es mono. Expórtala de nuevo en un solo canal (mono).',
  'tipo-no-coincide': 'El archivo no es del tipo que elegiste.',
  'demasiado-grande': 'El archivo pesa más de lo permitido para su tipo (PDF 100 MB; foto 5 MB, o 20 MB si es HEIC; nota de voz 16 MB, o 50 MB si es M4A, MP3 o WAV).',
  'conversion-fallida': 'No se pudo convertir el archivo. Expórtalo de nuevo o súbelo en otro formato.',
  'convertido-demasiado-grande': 'Ya convertido, el archivo sigue pesando más de lo que WhatsApp admite (foto 5 MB, nota de voz 16 MB). Súbelo más corto o con menos resolución.',
  'clave-invalida': 'La clave sólo puede llevar minúsculas, números y guiones bajos (de 3 a 60 caracteres, sin dos guiones bajos seguidos).',
  'grupo-distinto': 'Las fotos de un grupo tienen que tener la misma categoría y subcategoría que la primera. Déjalas vacías y se usarán las de la primera foto.',
});

const AVISOS = Object.freeze({
  'voz-sin-boton': 'La nota de voz pesa más de 512 KB: se enviará, pero WhatsApp la mostrará con un icono de descarga en vez del botón de reproducir.',
  'descarga-lenta': 'El archivo pesa más de 10 MB: tus clientes tardarán en descargarlo.',
  'convertido-a-ogg': 'El audio se convierte a nota de voz de WhatsApp (OGG Opus, mono) al subirlo.',
  'convertido-a-jpg': 'La foto HEIC se convierte a JPG al subirla.',
});

/** Los avisos (no bloqueantes) de un archivo de este tipo y tamaño. El servidor da los mismos. */
function avisosPorTamano(tipo, bytes) {
  const avisos = [];
  if (tipo === 'audio' && bytes > VOZ_CON_BOTON_MAX) avisos.push('voz-sin-boton');
  if (bytes >= AVISO_PESADO_DESDE) avisos.push('descarga-lenta');
  return avisos;
}

/**
 * Lo que la UI sabe ANTES de subir, sólo con lo que el navegador dice del archivo (nombre, tipo,
 * tamaño). NO sustituye a la validación por bytes del servidor: la adelanta para no hacer subir lo
 * que seguro se rechaza.
 * @param {{name?: string, type?: string, size?: number}} archivo
 * @returns {{tipo, mime, bloquea: string|null, avisos: string[]}}
 */
function antesDeSubir(archivo) {
  const nombre = String(archivo?.name || '').toLowerCase();
  const declarado = String(archivo?.type || '').toLowerCase();
  const bytes = Number(archivo?.size) || 0;
  // Los navegadores no se ponen de acuerdo con el OGG: `audio/ogg`, `video/ogg`, `application/ogg`
  // o vacío. La extensión decide; el servidor confirma por los bytes que es Opus.
  let mime = null;
  if (declarado === 'application/pdf' || nombre.endsWith('.pdf')) mime = 'application/pdf';
  else if (declarado === 'image/jpeg' || /\.(jpe?g)$/.test(nombre)) mime = 'image/jpeg';
  else if (declarado === 'image/png' || nombre.endsWith('.png')) mime = 'image/png';
  else if (/^(audio|video|application)\/ogg$/.test(declarado) || /\.(ogg|opus)$/.test(nombre)) mime = 'audio/ogg';
  // Los convertibles: el navegador tampoco se pone de acuerdo (`audio/x-m4a`, `audio/x-wav`, un HEIC sin tipo).
  else if (/^audio\/(mp4|x-m4a|m4a)$/.test(declarado) || nombre.endsWith('.m4a')) mime = 'audio/mp4';
  else if (/^audio\/(mpeg|mp3)$/.test(declarado) || nombre.endsWith('.mp3')) mime = 'audio/mpeg';
  else if (/^audio\/(wav|x-wav|wave|vnd\.wave)$/.test(declarado) || nombre.endsWith('.wav')) mime = 'audio/wav';
  else if (/^image\/(heic|heif)$/.test(declarado) || /\.(heic|heif)$/.test(nombre)) mime = 'image/heic';
  const tipo = mime ? TIPOS.find((t) => MIMES_POR_TIPO[t].includes(mime)) : null;
  if (!tipo) return { tipo: null, mime: null, bloquea: 'formato-no-admitido', avisos: [] };
  if (bytes <= 0) return { tipo, mime, bloquea: 'vacio', avisos: [] };
  if (bytes > limiteDeEntrada(tipo, mime)) return { tipo, mime, bloquea: 'demasiado-grande', avisos: [] };
  // Un convertible: el tamaño que cuenta es el del convertido, que sólo sabe el servidor.
  if (CONVERTIBLES[mime]) return { tipo, mime, bloquea: null, avisos: [tipo === 'audio' ? 'convertido-a-ogg' : 'convertido-a-jpg'] };
  return { tipo, mime, bloquea: null, avisos: avisosPorTamano(tipo, bytes) };
}

/**
 * ¿Puede este usuario SUBIR y BORRAR? owner/admin = `canManageIntegrations` (decisión de Rodrigo,
 * 28-oct); fuera de una organización el usuario es el dueño. Ver es `canViewBots` (la regla).
 * Owner por ROL, como el resto de gates de la app.
 */
function permisosDeLaBiblioteca(context) {
  const personal = !context || context.mode !== 'organization';
  const owner = context?.member?.role === 'owner';
  const p = context?.member?.permissions || {};
  return {
    gestionar: personal || owner || p.canManageIntegrations === true,
    ver: personal || owner || p.canViewBots === true,
  };
}

/**
 * La lista de la pestaña: una fila por CLAVE (un PDF, un audio, o un grupo de fotos con sus fotos en
 * orden), con los datos de la primera foto para el grupo y el total de bytes. Los documentos llegan
 * como los escribe `finalizarSubidaABiblioteca` (`{ id, clave, tipo, orden?, bytes, … }`).
 *
 * `categoria` y `subcategoria` son GENÉRICAS a propósito (29-oct, decisión de Rodrigo): sirven para
 * ordenar los archivos de cualquier negocio —un local, una línea de producto—, no sólo los salones y
 * tipos de evento de una productora. Ambas opcionales; el texto libre lo pone el cliente.
 */
function agruparPorClave(docs) {
  const porClave = new Map();
  for (const d of Array.isArray(docs) ? docs : []) {
    if (!d || typeof d.clave !== 'string') continue;
    if (!porClave.has(d.clave)) porClave.set(d.clave, []);
    porClave.get(d.clave).push(d);
  }
  return [...porClave.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([clave, archivos]) => {
      const ordenados = [...archivos].sort((a, b) => (a.orden || 0) - (b.orden || 0));
      const primero = ordenados[0];
      return {
        clave,
        tipo: primero.tipo,
        categoria: primero.categoria || null,
        subcategoria: primero.subcategoria || null,
        caption: primero.caption || null,
        bytes: ordenados.reduce((t, a) => t + (Number(a.bytes) || 0), 0),
        avisos: [...new Set(ordenados.flatMap((a) => (Array.isArray(a.avisos) ? a.avisos : [])))],
        lleno: primero.tipo === 'imagen' && ordenados.length >= MAX_FOTOS_POR_GRUPO,
        archivos: ordenados.map((a) => ({ id: a.id, orden: a.orden ?? null, bytes: Number(a.bytes) || 0, nombreArchivo: a.nombreArchivo || null })),
      };
    });
}

module.exports = {
  LIMITES, AVISO_PESADO_DESDE, VOZ_CON_BOTON_MAX, MAX_FOTOS_POR_GRUPO, TIPOS, MIMES_POR_TIPO, PATRON_CLAVE,
  CONVERTIBLES, LIMITES_DE_ENTRADA, limiteDeEntrada, MENSAJES, AVISOS, esClaveValida, avisosPorTamano, antesDeSubir, permisosDeLaBiblioteca, agruparPorClave,
};
